import { useGame } from "../em2c-store";
import { EM2C_BUILDINGS } from "../data/em2c-buildings";
import { axialToWorld, rotateFootprint } from "../hexgrid";
import { validatePlacement, freeHexes, occupyHexes, PLACEMENT } from "../hexplacement";
import { validateCommandNetworkPlacement } from "./CommandNetworkIntegration";
import { sfxClick, sfxError } from "../audio";

export const MOVE_BUILDING_COST = Object.freeze({ machine_parts: 2 });

export function canAffordMoveResources(resources = {}) {
  return Object.entries(MOVE_BUILDING_COST).every(([key, amount]) => (resources?.[key]?.stored || 0) >= amount);
}

export function buildMoveValidationGrid(state, buildingInstance) {
  const shadow = new Map(state.grid);
  const def = EM2C_BUILDINGS[buildingInstance?.type];
  if (!def || !buildingInstance) return shadow;

  const footprint = rotateFootprint(def.footprintHexes || [{ q: 0, r: 0 }], buildingInstance.rotation || 0);
  footprint.forEach(({ q, r }) => {
    const key = `${buildingInstance.q + q},${buildingInstance.r + r}`;
    const hex = shadow.get(key);
    if (hex?.buildingId === buildingInstance.id) {
      shadow.set(key, { ...hex, occupied: false, buildingId: null });
    }
  });
  return shadow;
}

function getMoveBuilding(state) {
  return state.buildings.find((building) => building.id === state.moveBuildingId) || null;
}

function validateMoveDestination(state, q, r) {
  const buildingInstance = getMoveBuilding(state);
  if (!buildingInstance) return { valid: false, reason: "No building selected to move", placement: PLACEMENT.BLOCKED };
  const def = EM2C_BUILDINGS[buildingInstance.type];
  if (!def) return { valid: false, reason: "Unknown building", placement: PLACEMENT.BLOCKED };
  if (buildingInstance.q === q && buildingInstance.r === r) {
    return { valid: false, reason: "Building is already here", placement: PLACEMENT.BLOCKED };
  }

  const shadowGrid = buildMoveValidationGrid(state, buildingInstance);
  const base = validatePlacement(shadowGrid, def, q, r, buildingInstance.rotation || 0);
  if (!base.valid) return base;

  // The moving structure cannot use its OWN current relay coverage to validate
  // its new destination. Excluding it prevents relay leap-frogging across Mars.
  const network = validateCommandNetworkPlacement(
    state,
    buildingInstance.type,
    q,
    r,
    buildingInstance.rotation || 0,
    buildingInstance.id,
  );
  return network.valid ? base : network;
}

export function installMoveBuildingIntegration() {
  const current = useGame.getState();
  if (current._moveBuildingIntegrationInstalled) return;

  useGame.setState({
    _moveBuildingIntegrationInstalled: true,
    moveBuildingId: null,
    moveCandidate: null,
    moveGridWasVisible: false,

    canAffordMoveBuilding: () => canAffordMoveResources(useGame.getState().resources),

    startMoveBuilding: (buildingId) => {
      const state = useGame.getState();
      const building = state.buildings.find((item) => item.id === buildingId);
      if (!building) return false;

      if (!canAffordMoveResources(state.resources)) {
        state.pushNotification?.({ level: "warning", text: "Need 2 Machine Parts to move a building" });
        sfxError();
        return false;
      }

      useGame.setState({
        placementType: null,
        hoverHex: null,
        moveBuildingId: buildingId,
        moveCandidate: null,
        moveGridWasVisible: Boolean(state.gridVisible),
        gridVisible: true,
        selectedId: buildingId,
      });
      state.pushNotification?.({ level: "info", text: `Move ${EM2C_BUILDINGS[building.type]?.name || "building"}: choose a destination hex` });
      sfxClick();
      return true;
    },

    cancelMoveBuilding: () => {
      const state = useGame.getState();
      if (!state.moveBuildingId) return;
      useGame.setState({
        moveBuildingId: null,
        moveCandidate: null,
        hoverHex: null,
        gridVisible: Boolean(state.moveGridWasVisible),
        moveGridWasVisible: false,
      });
    },

    isValidMovePlacement: (q, r) => validateMoveDestination(useGame.getState(), q, r),

    chooseMoveDestination: (q, r) => {
      const state = useGame.getState();
      if (!state.moveBuildingId) return false;
      const result = validateMoveDestination(state, q, r);
      if (!result.valid) {
        state.pushNotification?.({ level: "warning", text: result.reason || "Cannot move building here" });
        sfxError();
        return false;
      }
      useGame.setState({ moveCandidate: { q, r } });
      sfxClick();
      return true;
    },

    confirmMoveBuilding: () => {
      const state = useGame.getState();
      const buildingInstance = getMoveBuilding(state);
      const candidate = state.moveCandidate;
      if (!buildingInstance || !candidate) return false;

      const def = EM2C_BUILDINGS[buildingInstance.type];
      if (!def) return false;

      const result = validateMoveDestination(state, candidate.q, candidate.r);
      if (!result.valid) {
        state.pushNotification?.({ level: "warning", text: result.reason || "Destination is no longer available" });
        sfxError();
        return false;
      }
      if (!canAffordMoveResources(state.resources)) {
        state.pushNotification?.({ level: "warning", text: "Need 2 Machine Parts to complete the move" });
        sfxError();
        return false;
      }

      freeHexes(state.grid, def.footprintHexes || [{ q: 0, r: 0 }], buildingInstance.q, buildingInstance.r, buildingInstance.rotation || 0);
      occupyHexes(state.grid, def.footprintHexes || [{ q: 0, r: 0 }], candidate.q, candidate.r, buildingInstance.rotation || 0, buildingInstance.id);

      const world = axialToWorld(candidate.q, candidate.r);
      const resources = { ...state.resources };
      Object.entries(MOVE_BUILDING_COST).forEach(([key, amount]) => {
        if (resources[key]) resources[key] = { ...resources[key], stored: resources[key].stored - amount };
      });

      const movedBuilding = {
        ...buildingInstance,
        q: candidate.q,
        r: candidate.r,
        x: world.x,
        z: world.z,
      };

      useGame.setState({
        grid: new Map(state.grid),
        buildings: state.buildings.map((item) => item.id === movedBuilding.id ? movedBuilding : item),
        resources,
        selectedId: movedBuilding.id,
        moveBuildingId: null,
        moveCandidate: null,
        hoverHex: null,
        gridVisible: Boolean(state.moveGridWasVisible),
        moveGridWasVisible: false,
      });

      useGame.getState().pushNotification?.({ level: "info", text: `${def.name} moved immediately · 2 Machine Parts` });
      window.dispatchEvent(new CustomEvent("mars:focus-building", {
        detail: { buildingId: movedBuilding.id, q: movedBuilding.q, r: movedBuilding.r, x: movedBuilding.x, z: movedBuilding.z },
      }));
      sfxClick();
      return true;
    },
  });
}
